/**
 * Asset Uploader — dropzone component for S3 direct upload + existing file list with delete
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { uploadToS3 } from '../../services/uploadService';
import { api } from '../../services/api';
import type { UploadProgress } from '../../services/uploadService';

interface Props {
  projectId: string;
  assetType: string;
  accept: string;
  label: string;
  multiple?: boolean;
}

interface UploadState {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

interface ExistingFile {
  id: string;
  originalName: string;
  sizeBytes: string | null;
  status: string;
  createdAt: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function AssetUploader({ projectId, assetType, accept, label, multiple }: Props) {
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  const loadExistingFiles = useCallback(async () => {
    try {
      const files = await api.file.list(projectId, assetType);
      setExistingFiles(files.filter((f: any) => f.status !== 'uploading'));
    } catch (err) {
      console.error('[AssetUploader] Failed to load files:', err);
    }
  }, [projectId, assetType]);

  useEffect(() => {
    loadExistingFiles();
    // Auto-sync: link any orphaned S3 files to project JSONB
    api.upload.syncProject(projectId).catch(() => {});
  }, [loadExistingFiles, projectId]);

  const handleDelete = async (fileId: string) => {
    setDeletingIds((prev) => new Set(prev).add(fileId));
    try {
      await api.file.delete(fileId);
      setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      console.error('[AssetUploader] Delete failed:', err);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList) return;
    const files = Array.from(fileList);

    const newUploads: UploadState[] = files.map((f) => ({
      file: f,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploads((prev) => [...prev, ...newUploads]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const idx = uploads.length + i;

      setUploads((prev) => {
        const copy = [...prev];
        if (copy[idx]) copy[idx] = { ...copy[idx], status: 'uploading' };
        return copy;
      });

      try {
        await uploadToS3(file, projectId, assetType, (progress: UploadProgress) => {
          setUploads((prev) => {
            const copy = [...prev];
            if (copy[idx]) copy[idx] = { ...copy[idx], progress: progress.percent };
            return copy;
          });
        });

        setUploads((prev) => {
          const copy = [...prev];
          if (copy[idx]) copy[idx] = { ...copy[idx], status: 'complete', progress: 100 };
          return copy;
        });
      } catch (err: any) {
        setUploads((prev) => {
          const copy = [...prev];
          if (copy[idx]) copy[idx] = { ...copy[idx], status: 'error', error: err.message };
          return copy;
        });
      }
    }

    // Refresh existing files list after all uploads complete
    loadExistingFiles();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Existing files */}
      {existingFiles.length > 0 && (
        <div style={styles.existingList}>
          {existingFiles.map((f) => (
            <div key={f.id} style={styles.existingItem}>
              <span style={styles.fileName}>{f.originalName}</span>
              {f.sizeBytes && (
                <span style={styles.fileSize}>{formatBytes(Number(f.sizeBytes))}</span>
              )}
              <span style={styles.fileStatus}>{f.status}</span>
              <button
                onClick={() => handleDelete(f.id)}
                disabled={deletingIds.has(f.id)}
                style={styles.deleteBtn}
              >
                {deletingIds.has(f.id) ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      <div
        style={styles.dropzone}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
        <span style={styles.dropLabel}>{label}</span>
        <span style={styles.dropHint}>Click or drag files here</span>
      </div>

      {/* Active uploads */}
      {uploads.length > 0 && (
        <div style={styles.uploadList}>
          {uploads.map((u, i) => (
            <div key={i} style={styles.uploadItem}>
              <span style={styles.fileName}>{u.file.name}</span>
              {u.status === 'uploading' && (
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${u.progress}%` }} />
                </div>
              )}
              {u.status === 'complete' && <span style={{ color: '#22c55e', fontSize: 12 }}>Done</span>}
              {u.status === 'error' && <span style={{ color: '#ef4444', fontSize: 12 }}>{u.error || 'Failed'}</span>}
              {u.status === 'pending' && <span style={{ color: '#64748b', fontSize: 12 }}>Waiting...</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  dropzone: {
    border: '2px dashed #333333',
    borderRadius: 8,
    padding: '20px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  dropLabel: {
    color: '#94a3b8',
    fontSize: 14,
    fontWeight: 500,
  },
  dropHint: {
    color: '#475569',
    fontSize: 12,
  },
  existingList: {
    marginBottom: 12,
  },
  existingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #1a1a1a',
  },
  uploadList: {
    marginTop: 12,
  },
  uploadItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 0',
    borderBottom: '1px solid #1a1a1a',
  },
  fileName: {
    flex: 1,
    fontSize: 13,
    color: '#cbd5e1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  fileSize: {
    fontSize: 12,
    color: '#64748b',
    flexShrink: 0,
  },
  fileStatus: {
    fontSize: 11,
    color: '#475569',
    textTransform: 'uppercase' as const,
    flexShrink: 0,
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #7f1d1d',
    color: '#ef4444',
    borderRadius: 6,
    padding: '4px 12px',
    fontSize: 12,
    cursor: 'pointer',
    flexShrink: 0,
  },
  progressBar: {
    width: 120,
    height: 6,
    background: '#1a1a1a',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#4F7CFF',
    borderRadius: 3,
    transition: 'width 0.2s',
  },
};
